import { createSelector } from '@reduxjs/toolkit';
import { businessTariffsPromoQuotaMobile } from 'reactApp/appHelpers/featuresHelpers/features/businessTariffsPromoQuotaMobile';
import {
    multiTariffAdvancedTariffListMobile,
    multiTariffAdvancedTariffListTouchAnonymous,
    multiTariffBasicTariffListMobile,
    multiTariffBasicTariffListTouchAnonymous,
    multiTariffFamilyTariffListMobile,
    multiTariffFamilyTariffListTouchAnonymous,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import {
    priceDiffAdvancedTariffListMobile,
    priceDiffBasicTariffListMobile,
    priceDiffBusinessTariffListMobile,
    priceDiffFamilyTariffListMobile,
} from 'reactApp/appHelpers/featuresHelpers/features/priceDifferentiation';
import { BillingPersonalizationSelectors } from 'reactApp/modules/billingPersonalization/billingPersonalization.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getMultiTariffMobileActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';

export const getMultiTariffMobileProductsSpacesListByTab = createSelector(
    [
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffBasicTariffListMobile.year,
                ...multiTariffBasicTariffListMobile.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffAdvancedTariffListMobile.year,
                ...multiTariffAdvancedTariffListMobile.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffFamilyTariffListMobile.year,
                ...multiTariffFamilyTariffListMobile.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...businessTariffsPromoQuotaMobile.tariffs.year,
                ...businessTariffsPromoQuotaMobile.tariffs.month,
            ]),
        (state: RootState) => getMultiTariffMobileActiveTab(state),
    ],
    (basicTariffs, advancedTariffs, familyTariffs, businessTariffs, activeTab) => {
        let products: Record<number, { yearProduct: undefined | Product; monthProduct: undefined | Product }> = {};
        let yearProductIDs: string[] = [];

        if (activeTab === ETabsName.basic) {
            products = basicTariffs;
            yearProductIDs = multiTariffBasicTariffListMobile.year;
        } else if (activeTab === ETabsName.advanced) {
            products = advancedTariffs;
            yearProductIDs = multiTariffAdvancedTariffListMobile.year;
        } else if (activeTab === ETabsName.family) {
            products = familyTariffs;
            yearProductIDs = multiTariffFamilyTariffListMobile.year;
        } else if (activeTab === ETabsName.for_work) {
            products = businessTariffs;
            yearProductIDs = businessTariffsPromoQuotaMobile.tariffs.year;
        }

        const spaces = Object.values(products)
            .sort((a, b) => {
                if (a.yearProduct && b.yearProduct) {
                    return yearProductIDs.indexOf(a.yearProduct.id) - yearProductIDs.indexOf(b.yearProduct.id);
                }
                return 0;
            })
            .map((item) => item.yearProduct?.space.original || 0)
            .filter(Boolean);

        return { products, spaces, selectorDataLoaded: true };
    }
);

/* CLOUDWEB-20022 [Touch] Добавить мультитарифный лендинг на анонимов */
export const getMultiTariffMobileProductsSpacesListByTabForAnonymous = createSelector(
    [
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffBasicTariffListTouchAnonymous.year,
                ...multiTariffBasicTariffListTouchAnonymous.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffAdvancedTariffListTouchAnonymous.year,
                ...multiTariffAdvancedTariffListTouchAnonymous.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffFamilyTariffListTouchAnonymous.year,
                ...multiTariffFamilyTariffListTouchAnonymous.month,
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...businessTariffsPromoQuotaMobile.anonymousTariffs.year,
                ...businessTariffsPromoQuotaMobile.anonymousTariffs.month,
            ]),
        (state: RootState) => getMultiTariffMobileActiveTab(state),
    ],
    (basicTariffs, advancedTariffs, familyTariffs, businessTariffs, activeTab) => {
        let products: Record<number, { yearProduct: undefined | Product; monthProduct: undefined | Product }> = {};
        let yearProductIDs: string[] = [];

        if (activeTab === ETabsName.basic) {
            products = basicTariffs;
            yearProductIDs = multiTariffBasicTariffListTouchAnonymous.year;
        } else if (activeTab === ETabsName.advanced) {
            products = advancedTariffs;
            yearProductIDs = multiTariffAdvancedTariffListTouchAnonymous.year;
        } else if (activeTab === ETabsName.family) {
            products = familyTariffs;
            yearProductIDs = multiTariffFamilyTariffListTouchAnonymous.year;
        } else if (activeTab === ETabsName.for_work) {
            products = businessTariffs;
            yearProductIDs = businessTariffsPromoQuotaMobile.tariffs.year;
        }

        const spaces = Object.values(products)
            .sort((a, b) => {
                if (a.yearProduct && b.yearProduct) {
                    return yearProductIDs.indexOf(a.yearProduct.id) - yearProductIDs.indexOf(b.yearProduct.id);
                }
                return 0;
            })
            .map((item) => item.yearProduct?.space.original || 0)
            .filter(Boolean);

        return { products, spaces, selectorDataLoaded: true };
    }
);

// tempexp_19870-start
export const getMultiTariffMobilePriceDiff = createSelector(
    [
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(priceDiffBasicTariffListMobile?.year || []),
                ...(priceDiffBasicTariffListMobile?.month || []),
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(priceDiffAdvancedTariffListMobile?.year || []),
                ...(priceDiffAdvancedTariffListMobile?.month || []),
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(priceDiffFamilyTariffListMobile?.year || []),
                ...(priceDiffFamilyTariffListMobile?.month || []),
            ]),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(priceDiffBusinessTariffListMobile?.year || []),
                ...(priceDiffBusinessTariffListMobile?.month || []),
            ]),
        (state: RootState) => getMultiTariffMobileActiveTab(state),
    ],
    (basicTariffs, advancedTariffs, familyTariffs, businessTarrifs, activeTab) => {
        let products: Record<number, { yearProduct: undefined | Product; monthProduct: undefined | Product }> = {};
        let yearProductIDs: string[] = [];

        if (activeTab === ETabsName.basic) {
            products = basicTariffs;
            yearProductIDs = priceDiffBasicTariffListMobile?.year || [];
        } else if (activeTab === ETabsName.advanced) {
            products = advancedTariffs;
            yearProductIDs = priceDiffAdvancedTariffListMobile?.year || [];
        } else if (activeTab === ETabsName.family) {
            products = familyTariffs;
            yearProductIDs = priceDiffFamilyTariffListMobile?.year || [];
        } else if (activeTab === ETabsName.for_work) {
            products = businessTarrifs;
            yearProductIDs = priceDiffBusinessTariffListMobile?.year || [];
        }

        const spaces = Object.values(products)
            .sort((a, b) => {
                if (a.yearProduct && b.yearProduct) {
                    return yearProductIDs.indexOf(a.yearProduct.id) - yearProductIDs.indexOf(b.yearProduct.id);
                }
                return 0;
            })
            .map((item) => item.yearProduct?.space.original || 0)
            .filter(Boolean);

        return { products, spaces, selectorDataLoaded: true };
    }
);
// tempexp_19870-end

// tempexp_19915-start
export const getMultiTariffMobileBillingPersonalization = createSelector(
    [
        (state: RootState) => state,
        (state: RootState) => {
            const tariffIDs = BillingPersonalizationSelectors.getTariffIDsMobile(state);
            return ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(tariffIDs?.basic?.year || []),
                ...(tariffIDs?.basic?.month || []),
            ]);
        },
        (state: RootState) => {
            const tariffIDs = BillingPersonalizationSelectors.getTariffIDsMobile(state);
            return ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(tariffIDs?.advanced?.year || []),
                ...(tariffIDs?.advanced?.month || []),
            ]);
        },
        (state: RootState) => {
            const tariffIDs = BillingPersonalizationSelectors.getTariffIDsMobile(state);
            return ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(tariffIDs?.family?.year || []),
                ...(tariffIDs?.family?.month || []),
            ]);
        },
        (state: RootState) => getMultiTariffMobileActiveTab(state),
    ],
    (state, basicTariffs, advancedTariffs, familyTariffs, activeTab) => {
        let products: Record<number, { yearProduct: undefined | Product; monthProduct: undefined | Product }> = {};
        let yearProductIDs: string[] = [];

        const tariffIDs = BillingPersonalizationSelectors.getTariffIDsMobile(state);

        if (activeTab === ETabsName.basic) {
            products = basicTariffs;
            yearProductIDs = tariffIDs?.basic?.year || [];
        } else if (activeTab === ETabsName.advanced) {
            products = advancedTariffs;
            yearProductIDs = tariffIDs?.advanced?.year || [];
        } else if (activeTab === ETabsName.family) {
            products = familyTariffs;
            yearProductIDs = tariffIDs?.family?.year || [];
        }

        const spaces = Object.values(products)
            .sort((a, b) => {
                if (a.yearProduct && b.yearProduct) {
                    return yearProductIDs.indexOf(a.yearProduct.id) - yearProductIDs.indexOf(b.yearProduct.id);
                }
                return 0;
            })
            .map((item) => item.yearProduct?.space.original || 0)
            .filter(Boolean);

        return { products, spaces, selectorDataLoaded: true };
    }
);
// tempexp_19915-end
